import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Monitor, Calendar, Clock, Users, MapPin, ArrowRight, Star, Flame, Archive } from 'lucide-react';
import { trainings } from '../data/trainings';

// Re-export for detail page
export { trainings };

// Filter only upcoming trainings for main list
const upcomingTrainings = trainings.filter(t => t.status === 'upcoming');
const allTags = [...new Set(upcomingTrainings.flatMap(t => t.tags))];

const QuarterlyTrainingsPage = () => {
  const navigate = useNavigate();
  const [selectedTags, setSelectedTags] = useState([]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const filteredTrainings = selectedTags.length === 0
    ? upcomingTrainings
    : upcomingTrainings.filter(t => t.tags.some(tag => selectedTags.includes(tag)));

  // Sort trainings so cards with same number of instructors are adjacent
  // Single-instructor cards first, then multi-instructor cards
  const sortedTrainings = [...filteredTrainings].sort(
    (a, b) => a.instructors.length - b.instructors.length
  );

  return (
    <div className="min-h-screen pt-20" data-testid="quarterly-trainings-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20 relative overflow-hidden" data-testid="trainings-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-0 left-1/4" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <Link to="/services" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Services
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border border-[var(--primary)] bg-[var(--primary)]/10 flex items-center justify-center">
                <Monitor className="w-8 h-8 text-[var(--primary)]" />
              </div>
              <div>
                <p className="font-mono text-sm uppercase tracking-wider text-[var(--primary)]">Quarterly Programs</p>
                <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)]">Quarterly Trainings</h1>
              </div>
            </div>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">
              Join our intensive instructor-led training programs hosted quarterly. Each training features hands-on labs, real-world scenarios, and expert mentorship. Delivered online or at designated venues depending on the program.
            </p>
            {/* <Link to="/services/quarterly-trainings/archive" className="inline-flex items-center gap-2 mt-6 text-[var(--text-muted)] hover:text-[var(--primary)] font-mono text-sm border border-[var(--border)] px-4 py-2 hover:border-[var(--primary)] transition-colors" data-testid="view-archive-link">
              <Archive className="w-4 h-4" /> View Past Trainings
            </Link> */}
          </div>
        </div>
      </section>

      {upcomingTrainings.length === 0 ? (
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="hero-glow top-0 right-1/4" />
          <div className="container-main relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-[var(--surface)] border border-[var(--border)] p-8 md:p-12">
                <div className="space-y-6">
                  <p className="text-[var(--primary)] text-sm font-mono uppercase tracking-wider">Upcoming Programs</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)]">Something Interesting is Coming Soon</h2>
                  <p className="text-[var(--text-muted)] text-lg leading-relaxed">We're preparing some exclusive and cutting-edge training programs. Our team is curating world-class content to help you stay ahead in cybersecurity.</p>
                  <p className="text-[var(--text-muted)] text-base">Check out our past event programs while you wait for the next exciting batch of trainings.</p>
                </div>
              </div>
              <div className="bg-[var(--surface-highlight)] border border-[var(--border)] border-dashed p-8 md:p-12 flex flex-col items-center justify-center text-center space-y-4">
                <div className="text-5xl">🔐</div>
                <p className="text-[var(--text-muted)] text-lg">New trainings arriving soon</p>
                <p className="text-sm text-[var(--text-muted)]">Follow us for updates on the latest programs and special offerings.</p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Filter Section */}
          <section className="py-8 border-y border-[var(--border)] " data-testid="filter-section">
            <div className="container-main">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <span className="font-mono text-sm text-[var(--primary)] uppercase tracking-wider">Filter by Tag:</span>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`px-4 py-2 font-mono text-sm border transition-colors duration-200 ${
                        selectedTags.includes(tag)
                          ? 'border-[var(--primary)] bg-[var(--primary)]/20 text-[var(--primary)]'
                          : 'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--primary)]/50 hover:text-[var(--text-main)]'
                      }`}
                      data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {tag}
                    </button>
                  ))}
                  {selectedTags.length > 0 && (
                    <button
                      onClick={() => setSelectedTags([])}
                      className="px-4 py-2 font-mono text-sm border border-[var(--error)]/50 text-[var(--error)] hover:bg-[var(--error)]/10 transition-colors duration-200"
                      data-testid="clear-filters"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-4 text-sm text-[var(--text-muted)]">
                Showing {filteredTrainings.length} of {upcomingTrainings.length} upcoming trainings
              </p>
            </div>
          </section>

          {/* Trainings List */}
          <section className="py-16" data-testid="trainings-list">
            <div className="container-main">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {sortedTrainings.map((training) => (
                  <div key={training.id} className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--primary)] transition-colors duration-300 overflow-hidden group relative flex flex-col cursor-pointer" onClick={() => navigate(`/services/quarterly-trainings/${training.id}`)} data-testid={`training-card-${training.id}`}>
                    {/* Featured & Popular Badges */}
                    <div className="absolute top-4 right-4 flex gap-2 z-10">
                      {training.featured && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] text-xs font-mono uppercase" data-testid={`featured-badge-${training.id}`}>
                          <Star className="w-3 h-3 fill-[#FFD700]" /> Featured
                        </span>
                      )}
                      {training.popular && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-[#FF6B00]/20 border border-[#FF6B00] text-[#FF6B00] text-xs font-mono uppercase" data-testid={`popular-badge-${training.id}`}>
                          <Flame className="w-3 h-3" /> Popular
                        </span>
                      )}
                    </div>
                    <div className="p-6 border-b border-[var(--border)] flex-1 flex flex-col">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <span className={`inline-block px-3 py-1 text-xs font-mono uppercase tracking-wider mb-3 ${
                            training.level === 'Beginner' ? 'bg-[var(--primary)]/20 text-[var(--primary)]' :
                            training.level === 'Intermediate' ? 'bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]' :
                            training.level === 'Advanced' ? 'bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]' :
                            'bg-[var(--error)]/20 text-[var(--error)]'
                          }`}>{training.level}</span>
                          <h3 className="text-xl font-bold text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors duration-200">{training.title}</h3>
                        </div>
                        {/* <div className="text-right mt-8">
                          <p className="font-mono text-2xl font-bold text-[var(--primary)]">{training.price}</p>
                          <p className="text-xs text-[var(--text-muted)]">per person</p>
                        </div> */}
                      </div>
                      <p className="text-[var(--text-muted)] text-sm leading-relaxed flex-1">{training.description}</p>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Date</p><p className="text-sm text-[var(--text-main)] font-mono">{training.date}</p></div></div>
                        <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Duration</p><p className="text-sm text-[var(--text-main)] font-mono">{training.duration}</p></div></div>
                        {/* <div className="flex items-center gap-3"><Users className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Seats Left</p><p className="text-sm text-[var(--text-main)] font-mono">{training.seatsLeft}/{training.seats}</p></div></div> */}
                        <div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Training Type</p><p className="text-sm text-[var(--text-main)] font-mono">{training.venue}</p></div></div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {training.tags.map((tag, idx) => (
                          <span key={idx} className="px-2 py-1 bg-[var(--surface-highlight)] border border-[var(--border)] text-xs text-[var(--text-muted)] font-mono">{tag}</span>
                        ))}
                      </div>
                      {training.instructors.length === 1 ? (
                        <div className="pt-4 border-t border-[var(--border)] flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
                          <div className="flex items-center gap-4">
                            <div>
                              {training.instructors[0].photo ? (
                                <img
                                  src={training.instructors[0].photo}
                                  alt={training.instructors[0].name}
                                  title={training.instructors[0].name}
                                  className="w-16 h-16 rounded-full object-cover border-2 border-[var(--primary)] flex-shrink-0"
                                  loading="lazy"
                                  decoding="async"
                                />
                              ) : (
                                <div
                                  title={training.instructors[0].name}
                                  className="w-16 h-16 rounded-full bg-[var(--surface-highlight)] border-2 border-[var(--primary)] flex items-center justify-center font-mono text-[var(--primary)] text-lg font-bold flex-shrink-0"
                                >
                                  {training.instructors[0].name.split(' ').map(n => n[0]).join('')}
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-base font-semibold text-[var(--text-main)]">{training.instructors[0].name}</p>
                              <p className="text-xs text-[var(--text-muted)]">Instructor</p>
                              {(training.instructors[0].designation || training.instructors[0].company) && (
                                <p className="text-xs text-[var(--text-muted)] mt-1">
                                  {[training.instructors[0].designation, training.instructors[0].company].filter(Boolean).join(' @ ')}
                                </p>
                              )}
                            </div>
                          </div>
                          <Link to={`/services/quarterly-trainings/${training.id}`} onClick={(e) => e.stopPropagation()} className="bg-[var(--primary)] text-black font-mono font-bold px-6 py-3 text-sm uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)] w-full md:w-auto text-center md:text-left" data-testid={`know-more-btn-${training.id}`}>
                            Know More
                          </Link>
                        </div>
                      ) : (
                        <div className="pt-4 border-t border-[var(--border)] flex flex-col md:flex-row gap-4 md:items-end md:justify-between">
                          <div className="space-y-3">
                            {training.instructors.map((instructor, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <div>
                                  {instructor.photo ? (
                                    <img
                                      src={instructor.photo}
                                      alt={instructor.name}
                                      title={instructor.name}
                                      className="w-14 h-14 rounded-full object-cover border-2 border-[var(--primary)] flex-shrink-0"
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  ) : (
                                    <div
                                      title={instructor.name}
                                      className="w-14 h-14 rounded-full bg-[var(--surface-highlight)] border-2 border-[var(--primary)] flex items-center justify-center font-mono text-[var(--primary)] text-sm font-bold flex-shrink-0"
                                    >
                                      {instructor.name.split(' ').map(n => n[0]).join('')}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-[var(--text-main)]">{instructor.name}</p>
                                  {(instructor.designation || instructor.company) && (
                                    <p className="text-xs text-[var(--text-muted)]">
                                      {[instructor.designation, instructor.company].filter(Boolean).join(' @ ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <Link to={`/services/quarterly-trainings/${training.id}`} onClick={(e) => e.stopPropagation()} className="bg-[var(--primary)] text-black font-mono font-bold px-6 py-3 text-sm uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)] w-full md:w-auto md:flex-shrink-0 text-center md:text-left" data-testid={`know-more-btn-${training.id}`}>
                            Know More
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {filteredTrainings.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-[var(--text-muted)] text-lg">No trainings match the selected filters.</p>
                  <button onClick={() => setSelectedTags([])} className="mt-4 text-[var(--primary)] font-mono hover:underline">Clear filters</button>
                </div>
              )}
            </div>
          </section>
        </>
      )}
      <div className="container-main flex justify-center mb-12">
        <Link to="/services/quarterly-trainings/archive" className="inline-flex items-center gap-2 mt-6 text-[var(--text-muted)] hover:text-[var(--accent-purple)] font-mono text-sm border border-[var(--border)] px-4 py-2 hover:border-[var(--accent-purple)] transition-colors" data-testid="view-archive-link">
          <Archive className="w-4 h-4" /> View Past Programs
        </Link>
      </div>
    </div>
  );
};

export default QuarterlyTrainingsPage;
